import useManuscriptReportChoice from "../../../hooks/useManuscriptReportChoice";
import ManuscriptReportChoice from "../../ManuscriptReportChoice";
import { getBrowserTimeZone } from "../../../utils/getBrowserTimeZone";
import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useOnboardingStore, StepType, StepIds, Done, AnswersMap, Step, StepAnswer } from "../../../stores/onboardingStore";
import { SudoRadioButtons } from "../../../sudokit/components/SudoRadioButtons";
import { Text } from "../../../sudokit/components/Text";
import VStack from "../../../sudokit/Layout/VStack";
import { SudoButton } from "../../../sudokit/components/SudoButton";
import { MultiCheck } from "../../../sudokit/components/MultiCheck";
import { sendAppEvent } from "../../../Utils";
import Dropzone from "react-dropzone";
import classNames from "classnames";
import InlineSVG from "react-inlinesvg";
import Auth from "../../../Auth";
import { useToast } from "../../ToastWrapper";
import { createId } from "@paralleldrive/cuid2";
import useUserMetadata from "../../../hooks/useUserMetadata";
import MultiChoiceVStack from "./MultiChoiceVStack";
import { Textarea } from "../../../sudokit/components/Textarea";
import { Spinner } from "../../../sudokit/components/Spinner";
import { cn } from "../../../lib/cn";
import PluginCreationSteps from "./PluginCreationSteps";
import { MAX_IMPORT_WIP_INPUT_WORDS } from "../../../../../src/shared/constants";
import { setAgenticChatState } from "../../PersistentChat";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { CHAT_ONBOARDING_TOUR } from "../../StickyTour/Tours/ChatOnboardingTour";

// Segmented progress bar component
const ProgressBar: React.FC<{ currentStep: number; totalSteps: number }> = ({ currentStep, totalSteps }) => {
  return (
    <div className="w-full flex justify-center mb-8">
      <div className="w-1/4 flex bg-app-grey-3 rounded-full">
        {Array.from({ length: totalSteps }, (_, index) => (
          <div
            key={index}
            className={`flex-1 h-1 rounded-full transition-all duration-300 ease-out -ml-1 first:ml-0 ${index <= currentStep ? "bg-app-primary-cta-light" : "transparent"}`}
          />
        ))}
      </div>
    </div>
  );
};

interface QuestionnaireProps {
  setHasImportedOrSkipped: (value: boolean) => void;
  userData: any; // Replace 'any' with proper type if available
  surveyCompleted: boolean;
  subscription: any; // Replace 'any' with proper type if available
}

const Questionnaire: React.FC<QuestionnaireProps> = ({ setHasImportedOrSkipped, userData, surveyCompleted, subscription }) => {
  const { currentStepId, next, skipOnboarding, startOnboarding, isActive, recordAnswer, answers, flow, stepIdToIndex, back, markAsCompleted } = useOnboardingStore();

  const steps: Record<StepIds, Step<StepIds, AnswersMap>> = flow.steps;
  const { setToast } = useToast();
  const [importing, setImporting] = useState(false);
  const reportChoice = useManuscriptReportChoice();
  const maxImportWords = MAX_IMPORT_WIP_INPUT_WORDS;
  const { refetch, isLegacyProjectFlowTreatment } = useUserMetadata();
  const history = useHistory();
  // While the chat onboarding completion flow runs, show the "writing partner
  // getting ready" loading state instead of the default home redirect — the
  // flow navigates to the new project itself when the opener is ready.
  const [chatOnboardingInProgress, setChatOnboardingInProgress] = useState(false);
  const getMessage = (error: string) => {
    const message = (() => {
      switch (error) {
        case "Invalid file type":
          return "Could not upload your file. Please upload a .txt, .doc, .docx, .rtf, or .odt file.";
        case "Max words exceeded":
          return `Could not import your project because it exceeds the maximum word count. Please upload a file with ${maxImportWords.toLocaleString()} words or fewer.`;
        case "Max imports for trial user exceeded.":
          return `You can only import 2 novels while on your trial. Subscribe to import more.`;
        default:
          return "Could not import your project. Please try again.";
      }
    })();

    return (
      <>
        <InlineSVG
          src="/assets/close.svg"
          className="failed"
        />
        {message}
      </>
    );
  };

  const createImportedProject = async (file: File) => {
    try {
      const { folder: homeFolder } = await Auth.fetchWithIdToken("/api/folders/home", {});
      if (!homeFolder?.id) {
        throw new Error("Could not find home folder");
      }

      const projectId = createId();
      const title = file.name.replace(/\.[^/.]+$/, "");
      const { project } = await Auth.fetchWithIdToken("/api/projects/import", {
        title,
        source: "import-wip",
        folderParentId: homeFolder.id,
        folderProjectId: projectId,
      });

      if (!project?.id) {
        throw new Error("Invalid project response");
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", project.id);
      const result = await Auth.postFilesWithIdToken("/api/import", { projectId: project.id }, formData);
      await userData.update({ needsImport: false, hasImportedOrSkipped: true });
      if (!result?.document?.id) {
        throw new Error("Invalid document response");
      }

      return { projectId: project.id as string, documentId: result.document.id as string };
    } catch (error: any) {
      setImporting(false);
      setToast(getMessage(error?.message));
      throw error;
    }
  };

  const isStep = currentStepId !== null && currentStepId !== Done;
  const canProceed = isStep ? !!answers[currentStepId as StepIds] : false;
  const currentStep = isStep ? steps[currentStepId as StepIds] : undefined;
  const currentStepIndex = isStep ? stepIdToIndex[currentStepId as StepIds] : 0;

  const canProceedBrainDump = currentStepId === StepIds.Braindump && (answers[StepIds.Braindump] || answers[StepIds.BraindumpNotSure] != null);

  const handleNext = useCallback(() => {
    if (!currentStepId || currentStepId === Done) return;
    next();
  }, [currentStepId, next]);

  const handleImport = async (documentId: string, projectId: string, originalFilename?: string) => {
    if (!documentId || !projectId) {
      setImporting(false);
      setToast(getMessage("Could not import your project"));
      throw new Error("Missing required IDs for import");
    }

    try {
      await Auth.fetchWithIdToken("/api/ai/storyEngine/importNovel", { projectId, documentId, originalFilename, timeZone: getBrowserTimeZone(), enableImportReport: reportChoice.enableImportReport });
      sendAppEvent("onboarding_import_completed");
      recordAnswer(StepIds.ImportStep, "imported");
      next();
    } catch (err: any) {
      const error = err?.response ? (await err.response.json()).error : err?.message;
      setToast(getMessage(error));
      throw err;
    }
  };

  const handleDecline = async () => {
    sendAppEvent("signup_import_novel_skip_clicked");
    recordAnswer(StepIds.ImportStep, "skipped");

    await userData.update({ needsImport: false, hasImportedOrSkipped: true });
    handleNext();
  };

  useEffect(() => {
    sendAppEvent("survey_step_viewed", {
      step_name: currentStepId,
      survey_variation: "IMPROVE_ONBOARDING_V1",
    });
  }, [currentStepId]);

  useEffect(() => {
    if (!isActive) {
      startOnboarding();
      // Debugging purposes
      // window.next = next;
      // window.back = back;
    }
  }, [isActive, startOnboarding]);

  // Default chat onboarding for eligible users: instead of landing on the
  // home screen and starting the sticky-note tours, create a chat opener that
  // addresses the user's first concern and drop them straight into the
  // project with that conversation open.
  const completeWithChatOnboarding = async () => {
    sendAppEvent("Onboarding - Chat Onboarding Started", { survey_variation: "IMPROVE_ONBOARDING_V1" });

    const result = await Auth.fetchWithIdToken("/api/ai/onboarding/createProjectWithChatOpener", {});
    if (!result?.success || !result.projectId || !result.chatId) {
      throw new Error("createProjectWithChatOpener returned an invalid response");
    }

    // Refresh user metadata first: it now carries survey.completedAt (keeps
    // the legacy survey modal closed) and experiments.chat_onboarding (keeps
    // StickyNoteManager from auto-starting the control tours).
    await refetch();

    // Open the project directly onto the opener conversation, with a single
    // sticky pointing at the chat sidebar. Keep needsDemo until that tour ends.
    setAgenticChatState(result.projectId, result.chatId);
    useStickyTourStore.getState().startTour(CHAT_ONBOARDING_TOUR);
    history.push(`/app/project/${result.projectId}`);
  };

  useEffect(() => {
    if (currentStepId !== Done) return;
    if (isLegacyProjectFlowTreatment === undefined) return;
    setHasImportedOrSkipped(true);

    const run = async () => {
      // Persist the final survey before generating the opener from its answers.
      // Generate the legacy tour draft below only for the fallback/import flow.
      await markAsCompleted({ skipDraft: true });

      // Original Project UI treatment users never received a starter project.
      // Preserve their survey -> project home flow even if they finish later.
      if (isLegacyProjectFlowTreatment) {
        await refetch();
        history.push("/app");
        return;
      }

      if (answers[StepIds.ImportStep] !== "imported") {
        setChatOnboardingInProgress(true);
        try {
          await completeWithChatOnboarding();
          return;
        } catch (err) {
          Sentry.captureException(err);
          setChatOnboardingInProgress(false);
        }
      }

      // Legacy upload flow (and chat failure fallback): tour draft + home.
      Auth.fetchWithIdToken("/api/ai/generateOnboardingDraft", {}).catch((draftErr) => {
        Sentry.captureException(draftErr);
      });
      await refetch();
      history.push("/app");
    };

    run().catch((err) => {
      Sentry.captureException(err);
      history.push("/app");
    });
  }, [currentStepId, isLegacyProjectFlowTreatment]);

  useEffect(() => {
    const handleEnter = (event: KeyboardEvent) => {
      if (!canProceed || currentStep?.type === StepType.MultiChoiceCloud || currentStep?.type === StepType.MultiChoiceVStack || currentStep?.type === StepType.Custom) return;
      if (event.key === "Enter") {
        event.stopPropagation();
        handleNext();
      }
    };
    document.addEventListener("keydown", handleEnter);
    return () => document.removeEventListener("keydown", handleEnter);
  }, [handleNext, canProceed]);

  const handleOptionChange = (value: unknown) => {
    if (!currentStepId || currentStepId === Done) return;
    if (Array.isArray(value)) {
      if (value.every((v) => typeof v === "string")) {
        recordAnswer(currentStepId as StepIds, value as StepAnswer);
      }
      return;
    }
    if (typeof value === "string") {
      recordAnswer(currentStepId as StepIds, value as StepAnswer);
    }
  };

  // Format options based on step type
  const formatOptions = (step: Step<StepIds, AnswersMap>): { value: string; label: string; description?: string }[] => {
    if (step.type === StepType.SingleChoice || step.type === StepType.MultiChoiceVStack) {
      return step.options;
    }
    if (step.type === StepType.MultiChoiceCloud) {
      return step.options.map((option: string) => ({
        value: option.toLowerCase().replace(/[^a-z0-9]/g, "-"),
        label: option,
      }));
    }
    return [];
  };

  // Chat onboarding: the opener is being generated, keep the
  // user company until we land them in their project. Must render before the
  // surveyCompleted redirect below — the survey is already completed while
  // this flow finishes.
  if (chatOnboardingInProgress) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: "url(/assets/moonbeam-background.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <VStack
          spacing={4}
          align="center"
          className="text-center"
        >
          <Spinner size="lg" />
          <Text
            size="xl"
            color="grey-0"
            font="gt-super-text"
            align="center"
          >
            Setting up your first project…
          </Text>
          <Text
            size="base"
            color="grey-1"
            align="center"
          >
            Your writing partner is getting ready for you.
          </Text>
        </VStack>
      </div>
    );
  }

  if (surveyCompleted && !subscription.features.adminProductPreview) {
    history.push("/app");
    return null;
  }

  if (!isActive || currentStepId === Done) {
    return null;
  }
  return (
    <div
      className="min-h-screen flex items-start justify-center"
      style={{
        backgroundImage: "url(/assets/moonbeam-background.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-4xl pt-12 sm:pt-16 md:pt-24 lg:pt-40 xl:pt-48">
        <ProgressBar
          currentStep={currentStepIndex}
          totalSteps={Object.keys(stepIdToIndex).length}
        />

        <VStack
          spacing={6}
          align="center"
          className="text-center"
        >
          <VStack
            spacing={2}
            align="center"
            className={`text-center ${currentStepId !== StepIds.ImportStep && currentStepId !== StepIds.DescribePlugin && currentStepId !== StepIds.PluginResult ? "mb-4" : ""}`}
          >
            <Text
              size="3xl"
              color="grey-0"
              font="gt-super-text"
              align="center"
              className="text-xl tracking-normal leading-normal text-center text-app-grey-2 font-gt-super-text font-normal line-clamp-none max-w-5xl whitespace-pre-line"
            >
              {currentStepIndex === 0 ? "Hi, I’m Sudowrite, the world’s best writing partner!" : null}
            </Text>
            <Text
              size="3xl"
              color="grey-0"
              font="gt-super-text"
              align="center"
              className="max-w-5xl whitespace-pre-line"
            >
              {typeof currentStep?.title === "function" ? (currentStep.title as (answers: AnswersMap) => string)(answers) : currentStep?.title}
            </Text>
          </VStack>

          {currentStep?.subtitle && (
            <Text
              size="xl"
              weight="medium"
              color="grey-1"
              font="gt-super-text"
              className={`max-w-4xl text-center max-w-2xl ${currentStepId === StepIds.DescribePlugin || currentStepId === StepIds.PluginResult ? "-mt-3" : ""}`}
            >
              {typeof currentStep.subtitle === "function" ? (currentStep.subtitle as (answers: AnswersMap) => string)(answers) : currentStep.subtitle}
            </Text>
          )}
          {currentStep?.type === StepType.SingleChoice && (
            <div className="w-full max-w-xl">
              <SudoRadioButtons
                noBackground={true}
                options={formatOptions(currentStep)}
                value={(isStep ? (answers[currentStepId as StepIds] as string) : "") || ""}
                onChange={handleOptionChange}
                spacing={3}
                size="default"
              />
            </div>
          )}

          {currentStep?.type === StepType.MultiChoiceVStack && (
            <div className="w-full max-w-xl">
              <MultiChoiceVStack
                options={formatOptions(currentStep)}
                value={(isStep ? (answers[currentStepId as StepIds] as string[]) : []) || []}
                onChange={handleOptionChange}
                noBackground={true}
              />
              {currentStepId === StepIds.HelpType && (answers[StepIds.HelpType] as string[])?.includes("something-else") && (
                <div className="mt-2">
                  <input
                    type="text"
                    placeholder="Tell us what you're looking for..."
                    value={(answers[StepIds.HelpTypeSomethingElse] as string) ?? ""}
                    onChange={(e) => recordAnswer(StepIds.HelpTypeSomethingElse, e.target.value)}
                    className={cn(
                      "w-full px-2.5 py-2.5 rounded-lg border border-solid transition-all duration-150 box-border",
                      "bg-transparent text-app-grey-0 placeholder:text-app-grey-2",
                      "border-app-grey-2 focus:border-app-primary-cta-high-contrast",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-app-primary-cta-high-contrast",
                    )}
                    autoFocus
                  />
                </div>
              )}
            </div>
          )}

          {currentStep?.type === StepType.MultiChoiceCloud && (
            <div className="w-full max-w-2xl">
              <MultiCheck
                options={formatOptions(currentStep)}
                value={(isStep ? (answers[currentStepId as StepIds] as string[]) : []) || []}
                onChange={handleOptionChange}
                className="justify-center"
              />
            </div>
          )}

          {(currentStepId === StepIds.DescribePlugin || currentStepId === StepIds.PluginResult) && (
            <PluginCreationSteps />
          )}

          {currentStep?.type === StepType.Custom && currentStepId === StepIds.ImportStep && (
            <div className="w-full max-w-2xl">
              <div className="flex flex-col w-full h-full pt-2 gap-4 items-center">
                <ManuscriptReportChoice choice={reportChoice} disabled={importing} />
                <Dropzone
                  aria-label="import"
                  accept={{
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx", ".doc"],
                    "text/plain": [".txt"],
                    "application/rtf": [".rtf"],
                    "application/vnd.oasis.opendocument.text": [".odt"],
                  }}
                  onDrop={async (acceptedFiles, rejectedFiles) => {
                    sendAppEvent("signup_import_novel_file_dropped");
                    if (rejectedFiles.length > 0) {
                      sendAppEvent("signup_import_novel_file_rejected");
                      setToast(getMessage("Invalid file type"));
                      return;
                    }
                    setImporting(true);
                    try {
                      const file = acceptedFiles[0];
                      const { projectId, documentId } = await createImportedProject(file);
                      await handleImport(documentId, projectId, file.name);
                    } catch (error) {
                      // errors handled in helpers
                    } finally {
                      setImporting(false);
                    }
                  }}
                  disabled={importing || !reportChoice.ready}
                >
                  {({ getRootProps, getInputProps, isDragReject, isDragAccept }) => (
                    <div
                      {...getRootProps({ onClick: () => sendAppEvent("signup_import_novel_click_to_browse") })}
                      className={classNames(
                        "h-full flex flex-col justify-center items-center text-[var(--primary-cta)] border-dashed rounded-2xl transition-colors duration-200 ease-in-out bg-[rgba(72,8,209,0.05)] border-[1.5px] border-[var(--primary-cta)] px-6 py-8 cursor-pointer w-[600px] focus:outline-none focus-visible:border-solid focus-visible:border-[2px] focus-visible:border-[var(--grey-2)]",
                        {
                          "border-[var(--danger-color)] bg-[rgba(255,88,98,0.1)]": isDragReject,
                          "border-[var(--primary-cta-high-contrast)] bg-[var(--primary-cta-transparent-15)]": isDragAccept,
                        },
                      )}
                    >
                      <input
                        {...getInputProps()}
                        className="focus:outline-none focus-visible:outline-none"
                      />
                      <InlineSVG
                        src="/assets/import.svg"
                        className="h-[24px] w-[24px] py-[9px] px-[8px] [&_path]:stroke-[var(--primary-cta)]"
                      />
                      {!importing && (
                        <>
                          <p className="text-base font-medium text-[var(--primary-cta)]">Import your book</p>
                          <p className="text-sm text-[var(--primary-cta)] opacity-50">(.docx, .txt, .rtf, .odt)</p>
                        </>
                      )}
                      {importing && <p className="text-sm text-[var(--primary-cta)]">Uploading your file...</p>}
                    </div>
                  )}
                </Dropzone>
                <SudoButton
                  variant="tertiaryGrey"
                  type="button"
                  aria-label="Skip import"
                  tabIndex={0}
                  onClick={handleDecline}
                  className="underline underline-offset-4"
                >
                  No thanks, I'll start from scratch
                </SudoButton>
                <div className="mt-20 h-full w-[600px] flex flex-col justify-center items-center text-app-grey-0 rounded-2xl transition-colors duration-200 ease-in-out border-solid border-[var(--menu-color-primary-cta-overlay-active)] px-6 py-6">
                  <InlineSVG
                    src="/assets/privacy-guarantee.svg"
                    className="-mt-[56px]"
                  />
                  <h3 className="font-gt-super-text font-medium text-2xl mb-2">Privacy Guarantee</h3>
                  <p>Sudowrite will never train AI models on your manuscript, sell your content to third parties, or use your work for any purpose without permission.</p>
                </div>
              </div>
            </div>
          )}

          {currentStepId === StepIds.Braindump && (
            <div className="w-full max-w-2xl">
              <Textarea
                mode="default"
                placeholder="Jot down anything you know about your story right now. You can always add or change things later."
                value={(answers[StepIds.Braindump] as string) ?? ""}
                onChange={(e) => {
                  recordAnswer(StepIds.Braindump, e.target.value);
                }}
              />
              <button
                type="button"
                role="checkbox"
                aria-checked={answers[StepIds.BraindumpNotSure] === true}
                onClick={() => {
                  sendAppEvent("Onboarding - Not Sure Checkbox Clicked", {
                    checked: answers[StepIds.BraindumpNotSure] !== "true",
                    has_braindump_text: Boolean(answers[StepIds.Braindump]),
                  });
                  recordAnswer(StepIds.BraindumpNotSure, answers[StepIds.BraindumpNotSure] === true ? false : true);
                }}
                className={cn(
                  "flex w-full items-center px-2.5 pb-3 pt-2 mt-3 duration-150 ease-in-out cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-cta-high-contrast",
                  "bg-transparent transition-all",
                  answers[StepIds.BraindumpNotSure] === true
                    ? "!border-app-primary-cta-high-contrast text-app-primary-cta-high-contrast"
                    : "!border-app-grey-2 text-app-grey-0 hover:text-app-grey-0 hover:!border-app-grey-1 active:scale-[0.995] hover:scale-[1.005]",
                )}
                tabIndex={0}
              >
                <div className="flex items-center gap-2 w-full">
                  <InlineSVG
                    src="/assets/check-selected.svg"
                    className={cn(
                      "transition-colors flex-none",
                      answers[StepIds.BraindumpNotSure] === true ? "fill-app-primary-cta-high-contrast [&>path]:fill-app-primary-cta-high-contrast" : "fill-app-grey-1 [&>path]:fill-app-grey-1",
                    )}
                  />
                  <Text
                    size="base"
                    weight="medium"
                    color={answers[StepIds.BraindumpNotSure] === true ? "primary-cta" : "grey-0"}
                    font="inter"
                  >
                    I'm not sure what my story is yet
                  </Text>
                </div>
              </button>
            </div>
          )}
          {currentStepId === StepIds.OnboardingDecision && JSON.stringify(answers)}
          {(canProceed || canProceedBrainDump) && currentStepId !== StepIds.DescribePlugin && currentStepId !== StepIds.PluginResult && (
            <SudoButton
              variant="primary"
              size="default"
              onClick={handleNext}
              className="mt-8 rounded-[8px] focus-visible:ring-2 focus-visible:ring-app-primary-cta-high-contrast focus-visible:ring-offset-2"
              tabIndex={0}
            >
              Continue
            </SudoButton>
          )}
        </VStack>
      </div>
    </div>
  );
};

export default Questionnaire;
